import { useMemo, useState } from 'react';
import { Header } from '@/components/Header';
import { useAppStore } from '@/stores/appStore';
import { createInstantlyCampaign } from '@/services/research';

type StepRow = {
  id: string;
  subject: string;
  body: string;
  delayDays: number;
};

function newStep(seed: number): StepRow {
  return {
    id: `step-${seed}`,
    subject: '',
    body: '',
    delayDays: 0,
  };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

function toPreviewHtml(value: string): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (looksLikeHtml(raw)) return raw;
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  return lines
    .map((line) => (line.trim() ? `<div>${escapeHtml(line)}</div>` : '<div><br /></div>'))
    .join('');
}

export function EmailCampaignBuilder() {
  const userEmail = useAppStore((s) => s.userEmail);
  const [campaignName, setCampaignName] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dailyLimit, setDailyLimit] = useState('50');
  const [emailGapMinutes, setEmailGapMinutes] = useState('5');
  const [stopOnReply, setStopOnReply] = useState(true);
  const [openTracking, setOpenTracking] = useState(true);
  const [linkTracking, setLinkTracking] = useState(true);
  const [steps, setSteps] = useState<StepRow[]>([newStep(1)]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    if (!campaignName.trim()) return false;
    return steps.some((step) => step.subject.trim() && step.body.trim());
  }, [campaignName, steps]);

  const updateStep = (id: string, patch: Partial<StepRow>) => {
    setSteps((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addStep = () => {
    setSteps((prev) => [...prev, newStep(prev.length + 1)]);
  };

  const removeStep = (id: string) => {
    setSteps((prev) => (prev.length <= 1 ? prev : prev.filter((row) => row.id !== id)));
  };

  const handleCreate = async () => {
    if (!userEmail) return;
    setSaving(true);
    setStatus(null);
    setCreatedCampaignId(null);
    try {
      const result = await createInstantlyCampaign({
        userEmail,
        name: campaignName,
        timezone,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        dailyLimit: Number(dailyLimit) || undefined,
        emailGapMinutes: Number(emailGapMinutes) || undefined,
        stopOnReply,
        openTracking,
        linkTracking,
        steps: steps.map((step) => ({
          subject: step.subject,
          body: step.body,
          delayDays: step.delayDays,
        })),
      });
      if (!result.success || !result.campaign?.id) {
        throw new Error(result.error || 'Instantly campaign create failed.');
      }
      setCreatedCampaignId(result.campaign.id);
      setStatus(`Campaign created: ${result.campaign.name || campaignName} (${result.campaign.id})`);
    } catch (error: any) {
      setStatus(error?.message || 'Failed to create campaign.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Campaign Builder</h1>
          <p className="text-gray-400 mt-2">Create Instantly campaigns with sequence steps from inside SimplyScrapable.</p>
        </div>

        {status && (
          <div className="rounded border border-gray-700 bg-gray-900/60 px-4 py-3 text-sm text-gray-200">
            {status}
          </div>
        )}

        <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold">Campaign Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Campaign Name</label>
              <input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Campaign Name"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Timezone</label>
              <input
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="Timezone (e.g. America/Chicago)"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Daily Send Limit</label>
              <input
                value={dailyLimit}
                onChange={(e) => setDailyLimit(e.target.value)}
                placeholder="Daily Send Limit"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Email Gap (minutes)</label>
              <input
                value={emailGapMinutes}
                onChange={(e) => setEmailGapMinutes(e.target.value)}
                placeholder="Email Gap (minutes)"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <label className="flex items-center gap-2 text-gray-300">
              <input type="checkbox" checked={stopOnReply} onChange={(e) => setStopOnReply(e.target.checked)} className="h-4 w-4 accent-red-500" />
              Stop On Reply
            </label>
            <label className="flex items-center gap-2 text-gray-300">
              <input type="checkbox" checked={openTracking} onChange={(e) => setOpenTracking(e.target.checked)} className="h-4 w-4 accent-red-500" />
              Open Tracking
            </label>
            <label className="flex items-center gap-2 text-gray-300">
              <input type="checkbox" checked={linkTracking} onChange={(e) => setLinkTracking(e.target.checked)} className="h-4 w-4 accent-red-500" />
              Link Tracking
            </label>
          </div>
        </section>

        <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Sequence Steps</h2>
            <button
              onClick={addStep}
              className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-xs text-white"
            >
              Add Step
            </button>
          </div>
          <div className="space-y-4">
            {steps.map((step, idx) => (
              <div key={step.id} className="rounded-lg border border-gray-800 bg-gray-900/50 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">Step {idx + 1}</div>
                  <button
                    onClick={() => removeStep(step.id)}
                    className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-xs text-gray-200"
                    disabled={steps.length <= 1}
                  >
                    Remove
                  </button>
                </div>
                <input
                  value={step.subject}
                  onChange={(e) => updateStep(step.id, { subject: e.target.value })}
                  placeholder="Subject"
                  className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-white"
                />
                <textarea
                  value={step.body}
                  onChange={(e) => updateStep(step.id, { body: e.target.value })}
                  rows={6}
                  placeholder="Email body"
                  className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-white"
                />
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Email Preview</label>
                  <div className="rounded border border-gray-800 bg-white text-gray-900 p-3 text-sm max-h-72 overflow-auto">
                    {step.body.trim() ? (
                      <div dangerouslySetInnerHTML={{ __html: toPreviewHtml(step.body) }} />
                    ) : (
                      <span className="text-gray-500">(No body)</span>
                    )}
                  </div>
                </div>
                <div className="space-y-1 w-56">
                  <label className="text-xs text-gray-400">Days Before Sending This Step</label>
                  <input
                    value={String(step.delayDays)}
                    onChange={(e) => updateStep(step.id, { delayDays: Math.max(0, Number(e.target.value) || 0) })}
                    placeholder="0"
                    className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-white"
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="flex items-center gap-3">
          <button
            onClick={handleCreate}
            disabled={saving || !canSubmit}
            className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
          >
            {saving ? 'Creating…' : 'Create Campaign In Instantly'}
          </button>
          {createdCampaignId && <span className="text-xs text-gray-400">Created campaign id: {createdCampaignId}</span>}
        </div>
      </main>
    </div>
  );
}
